import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Table,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import SummaryCards from "components/SummaryCards";
import CustomPagination from "components/CustomPagination";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { get, post, put } from "helpers/api_helper"; // Assuming same API helpers as Assignments
import {
  PUT_TEMPLATE_STATUS_ID,
  GET_TEMPLATES_COUNT,
  GET_ALL_TEMPLATE,
  GET_TEMPLATE_BY_ID,
} from "constants/api_constants";

// Hypothetical API constants
import secureLocalStorage from "react-secure-storage";

const Template = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [templatesData, setTemplatesData] = useState([]);
  console.log("templatesData", templatesData);

  const [templatesCount, setTemplatesCount] = useState({
    total: 0,
    inactive: 0,
    active: 0,
  });
  const [apiStatus, setApiStatus] = useState({ loading: false, error: "" });
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const timerRef = useRef(null);

  // Fetch templates and counts on mount
  useEffect(() => {
    fetchCounts();
    fetchTemplates();
  }, []);

  // debugger
  const toggleTemplateStatus = useCallback(
    async (tid) => {
      try {
        setApiStatus({ loading: true, error: "", errorTemplateId: null });

        // Get current status from templatesData
        const template = templatesData.find((t) => t.tid === tid);
        console.log("template", template);

        if (!template) {
          throw new Error("Template not found");
        }
        const currentStatus = template.status;
        // console.log(currentStatus);

        // Toggle status
        const newStatus = currentStatus === 1 ? 0 : 1;

        // Call update API
        await put(PUT_TEMPLATE_STATUS_ID(tid, newStatus));

        // Update local templatesData
        setTemplatesData((prev) =>
          prev.map((t) => (t.tid === tid ? { ...t, status: newStatus } : t))
        );

        // Refresh template counts
        const countData = await get(GET_TEMPLATES_COUNT);
        setTemplatesCount(
          countData?.data || {
            totalCount: 0,
            activeTemplates: 0,
            inactiveTemplates: 0,
          }
        );

        setApiStatus({ loading: false, error: "", errorTemplateId: null });
      } catch (error) {
        console.error("Error updating template status:", error);
        setApiStatus({
          loading: false,
          error: "Failed to update template status",
          errorTemplateId: tid,
        });
      }
    },
    [templatesData]
  );

  // Fetch counts with API status
  const fetchCounts = useCallback(async () => {
    try {
      setApiStatus({ loading: true, error: "" });
      const countData = await get(GET_TEMPLATES_COUNT);
      setTemplatesCount(countData?.data || {});
      setApiStatus({ loading: false, error: "" });
    } catch (error) {
      console.error("Error fetching template counts:", error);
      setApiStatus({
        loading: false,
        error: "Failed to fetch template counts",
      });
    }
  }, []);

  // Fetch templates with API status
  const fetchTemplates = useCallback(
    async (query = "", department = "", status = "", page = 1) => {
      try {
        setApiStatus({ loading: true, error: "" });
        const response = await post(GET_ALL_TEMPLATE, {
          searchKeyword: query,
          department: department || "",
          status: status || null,
          page: page - 1,
          pageSize: 10,
          orderBy: "templateId",
          sort: "desc",
        });
        setTemplatesData(response?.data?.data || []);

        setTotalPages(response?.data?.totalPages || 0);
        setApiStatus({ loading: false, error: "" });
      } catch (error) {
        console.error("Error fetching templates:", error);
        setApiStatus({ loading: false, error: "Failed to fetch templates" });
      }
    },
    []
  );

  // Page change handler
  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchTemplates(searchQuery, departmentFilter, statusFilter, page);
    },
    [fetchTemplates, searchQuery, departmentFilter, statusFilter]
  );

  // Search query handler with debouncing
  const handleSearchQuery = (e) => {
    setSearchQuery(e.target.value ?? "");
    const query = e.target.value || "";

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setCurrentPage(0); // Reset to first page on new search
      fetchTemplates(query, departmentFilter, statusFilter);
    }, 300);
  };

  // Department filter handler
  const handleDepartmentFilter = (e) => {
    setDepartmentFilter(e.target.value);
    setCurrentPage(0); // Reset to first page
    fetchTemplates(searchQuery, e.target.value, statusFilter);
  };

  // Status filter handler
  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(0); // Reset to first page
    fetchTemplates(searchQuery, departmentFilter, e.target.value);
  };

  // Status label and color determination
  const getStatusLabelAndColor = (status) => {
    switch (status) {
      case 1:
        return { label: "Active", color: "text-success fw-semibold" };
      case 0:
        return { label: "Inactive", color: "text-secondary fw-semibold" };
      default:
        return { label: status, color: "text-muted" };
    }
  };

  const summaryData = [
    {
      iconClass: "fas fa-list text-primary",
      label: "Total Templates",
      value: templatesCount.total,
    },
    {
      iconClass: "fas fa-check-circle text-success",
      label: "Active",
      value: templatesCount.active,
    },
    {
      iconClass: "fas fa-times-circle text-danger",
      label: "Inactive",
      value: templatesCount.inactive,
    },
  ];

  const goToAddNewTemplate = () => {
    navigate("/add-new-templete");
  };

  const handleView = (id) => {
    navigate(`/view-templete/${id}`);
  };

  return (
    <div className="page-content">
      <Container fluid className="py-0 px-0">
        <PageSubHeader
          heading={"Job Posting Templates"}
          routePath={"Home"}
          pageName={"Templates"}
        />
        <Row className="px-4 py-4">
          <SummaryCards data={summaryData} />
          <Row className="align-items-center">
            <Col className="d-flex p-2 mx-2">
              <InputGroup
                size="sm"
                style={{ width: "348px" }}
                className="elbert-search-group"
              >
                <Input
                  placeholder="Search by ID, Template Name"
                  className="elbert-search"
                  value={searchQuery}
                  onChange={handleSearchQuery}
                />
                <InputGroupText>
                  <span className="fa fa-search"></span>
                </InputGroupText>
              </InputGroup>
              <Input
                type="select"
                className="elbert-search"
                style={{ width: "125px", marginLeft: "16px" }}
                value={departmentFilter}
                onChange={handleDepartmentFilter}
              >
                <option value="">Department</option>
                <option value="Engineering">Engineering</option>
                <option value="Designer">Designer</option>
                <option value="Tester">Tester</option>
                <option value="Human Resources">Human Resources</option>
                <option value="Marketing">Marketing</option>
              </Input>
              <Input
                type="select"
                className="elbert-search"
                style={{ width: "125px", marginLeft: "16px" }}
                value={statusFilter}
                onChange={handleStatusFilter}
              >
                <option value="">Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Input>
            </Col>
            <Col className="d-flex gap-3 align-items-center justify-content-end">
              <CustomButton text={"+ Add New"} onClick={goToAddNewTemplate} />
            </Col>
          </Row>
          <Row className="mt-4 px-4">
            <Card>
              <CardBody>
                {apiStatus.loading && <div>Loading...</div>}
                {apiStatus.error && (
                  <div className="text-danger">{apiStatus.error}</div>
                )}
                <div className="table-responsive">
                  <Table
                    className="table mb-0"
                    style={{ border: "0.4px solid #D4D4D4" }}
                  >
                    <thead>
                      <tr>
                        <th></th>
                        <th>Template ID</th>
                        <th>Template Name</th>
                        <th>Department</th>
                        <th>Experience</th>
                        <th>Created By</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templatesData.length > 0 ? (
                        templatesData.map((template) => {
                          const { label, color } = getStatusLabelAndColor(
                            template.status
                          );
                          return (
                            <tr key={template.templateId}>
                              <td>
                                {/* <Input
                                  type="checkbox"
                                  style={{ width: "17px", height: "17px" }}
                                /> */}
                              </td>
                              <td>{template.templateId}</td>
                              <td>{template.templateName}</td>
                              <td>{template.department}</td>
                              <td>{template.experienceRequired}</td>
                              <td>{template.createBy}</td>
                              <td className={color}>
                                <div>
                                  <FormGroup switch>
                                    <Label
                                      for={`templateStatus-${template.templateId}`}
                                      className={color}
                                    ></Label>

                                    <Input
                                      style={{
                                        color: "blue",
                                        height: "1.4em",
                                        width: "3em",
                                        cursor: "pointer",
                                      }}
                                      type="switch"
                                      disabled={apiStatus.loading}
                                      id={`templateStatus-${template.tid}`}
                                      checked={template.status === 1}
                                      onChange={() =>
                                        toggleTemplateStatus(template.tid)
                                      }
                                    />
                                  </FormGroup>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <i
                                    className="fa-solid fa-eye action-icon"
                                    title="View"
                                    style={{
                                      cursor: "pointer",
                                      display: "flex",
                                      justifyContent: "center",
                                      marginLeft: "5px",
                                    }}
                                    onClick={() =>
                                      navigate(`/view-templete/${template.tid}`)
                                    }
                                  ></i>
                                  <i
                                    className="fa-regular fa-pen-to-square action-icon"
                                    title="Edit"
                                    style={{
                                      cursor: "pointer",
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                    onClick={() =>
                                      navigate(
                                        `/add-new-templete/${template.tid}`
                                      )
                                    }
                                  ></i>
                                  {/* <i
                                    className="fa-solid fa-xmark action-icon"
                                    title="Close"
                                    style={{
                                      cursor: "pointer",
                                      display: "flex",
                                      justifyContent: "center",
                                      fontSize: "16px",
                                      color: "#F70D0D",
                                    }}
                                    onClick={() =>
                                      console.log(
                                        `Close template ${template.templateId}`
                                      )
                                    }
                                  ></i> */}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No templates found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Row>
      </Container>
    </div>
  );
};

export default Template;
